import { reactive } from 'vue'
import { markUx } from './metrics'
import { prefetchNavigation } from './navigationPrefetch'
import type { AppRoute, RouteName } from './types'

function clean(value: string | undefined): string | undefined {
  return value === undefined ? undefined : decodeURIComponent(value)
}

export function parseRoute(url: URL): AppRoute {
  const parts = url.pathname.split('/').filter(Boolean)
  const query = new URLSearchParams(url.search)
  if (parts.length === 0 || parts[0] === 'login')
    return { name: 'login', path: url.pathname, query }
  if (parts[0] === 'worlds')
    return { name: 'worlds', path: url.pathname, query }
  if (parts[0] === 'settings')
    return { name: 'settings', path: url.pathname, query }
  if (parts[0] === 'account')
    return { name: 'account', path: url.pathname, query }
  if (parts[0] === 'security')
    return { name: 'security-center', path: url.pathname, query }
  if (parts[0] === 'profile' && parts[1])
    return {
      name: 'public-profile',
      path: url.pathname,
      accountId: clean(parts[1]),
      query,
    }
  if (parts[0] === 'social')
    return { name: 'social', path: url.pathname, query }
  if (parts[0] === 'notifications')
    return { name: 'notifications', path: url.pathname, query }
  if (
    parts[0] === 'admin' &&
    (parts[1] === 'design' || parts[1] === 'profiles')
  )
    return { name: 'admin-design', path: url.pathname, query }
  if (
    parts[0] === 'admin' &&
    parts[1] === 'world-templates' &&
    parts[2] === 'new'
  )
    return { name: 'admin-world-template-editor', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'world-templates' && parts[2])
    return {
      name: 'admin-world-template-editor',
      path: url.pathname,
      adminWorldTemplateId: clean(parts[2]),
      query,
    }
  if (parts[0] === 'admin' && parts[1] === 'world-templates')
    return { name: 'admin-world-templates', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'worlds' && parts[2] === 'new')
    return { name: 'admin-world-create', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'worlds' && parts[2])
    return {
      name: 'admin-world-detail',
      path: url.pathname,
      adminWorldId: clean(parts[2]),
      query,
    }
  if (parts[0] === 'admin' && parts[1] === 'worlds')
    return { name: 'admin-worlds', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'accounts' && parts[2])
    return {
      name: 'admin-account-detail',
      path: url.pathname,
      adminAccountId: clean(parts[2]),
      query,
    }
  if (parts[0] === 'admin' && parts[1] === 'accounts')
    return { name: 'admin-accounts', path: url.pathname, query }
  if (
    parts[0] === 'admin' &&
    parts[1] === 'trust-safety' &&
    parts[2] === 'cases' &&
    parts[3]
  )
    return {
      name: 'admin-trust-safety-case',
      path: url.pathname,
      adminCaseId: clean(parts[3]),
      query,
    }
  if (
    parts[0] === 'admin' &&
    parts[1] === 'trust-safety' &&
    parts[2] === 'appeals' &&
    parts[3]
  )
    return {
      name: 'admin-trust-safety-appeal',
      path: url.pathname,
      adminAppealId: clean(parts[3]),
      query,
    }
  if (
    parts[0] === 'admin' &&
    parts[1] === 'trust-safety' &&
    parts[2] === 'appeals'
  )
    return { name: 'admin-trust-safety-appeals', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'trust-safety')
    return { name: 'admin-trust-safety', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'gm')
    return { name: 'admin-gm', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'audit')
    return { name: 'admin-audit', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'content')
    return { name: 'admin-content', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'balance')
    return { name: 'admin-balance', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'operations')
    return { name: 'admin-operations', path: url.pathname, query }
  if (parts[0] === 'admin' && parts[1] === 'tools') {
    const legacyGroup = query.get('group')
    if (legacyGroup === 'content')
      return { name: 'admin-content', path: url.pathname, query }
    if (legacyGroup === 'balance')
      return { name: 'admin-balance', path: url.pathname, query }
    if (legacyGroup === 'assets')
      return { name: 'admin-operations', path: url.pathname, query }
    return { name: 'admin-tools', path: url.pathname, query }
  }
  if (parts[0] === 'admin')
    return { name: 'admin-hub', path: url.pathname, query }
  if (parts[0] === 'world' && parts[1]) {
    const worldId = clean(parts[1])
    if (parts[2] === 'dashboard')
      return { name: 'world-dashboard', path: url.pathname, worldId, query }
    if (parts[2] === 'map')
      return { name: 'map', path: url.pathname, worldId, query }
    if (parts[2] === 'movements')
      return { name: 'movements', path: url.pathname, worldId, query }
    if (parts[2] === 'reports')
      return { name: 'reports', path: url.pathname, worldId, query }
    if (parts[2] === 'messages')
      return { name: 'messages', path: url.pathname, worldId, query }
    if (parts[2] === 'chat')
      return { name: 'chat', path: url.pathname, worldId, query }
    if (parts[2] === 'alliance' && parts[3] === 'forum' && parts[4])
      return {
        name: 'forum-topic',
        path: url.pathname,
        worldId,
        topicId: clean(parts[4]),
        query,
      }
    if (parts[2] === 'alliance' && parts[3] === 'forum')
      return { name: 'alliance-forum', path: url.pathname, worldId, query }
    if (parts[2] === 'alliance')
      return { name: 'alliance', path: url.pathname, worldId, query }
    if (parts[2] === 'quests')
      return { name: 'quests', path: url.pathname, worldId, query }
    if (parts[2] === 'inventory')
      return { name: 'inventory', path: url.pathname, worldId, query }
    if (parts[2] === 'rankings')
      return { name: 'rankings', path: url.pathname, worldId, query }
    if (parts[2] === 'victory')
      return { name: 'victory', path: url.pathname, worldId, query }
    if (parts[2] === 'notifications')
      return { name: 'notifications', path: url.pathname, worldId, query }
    if (parts[2] === 'social')
      return { name: 'social', path: url.pathname, worldId, query }
    if (parts[2] === 'advanced')
      return { name: 'advanced', path: url.pathname, worldId, query }
    if (parts[2] === 'village' && parts[3]) {
      const villageId = clean(parts[3])
      const feature = parts[4] ?? 'village'
      if (feature === 'buildings' && parts[5]) {
        return {
          name: 'building',
          path: url.pathname,
          worldId,
          villageId,
          buildingType: clean(parts[5]),
          query,
        }
      }
      const names: Record<string, RouteName> = {
        village: 'village',
        dashboard: 'dashboard',
        buildings: 'buildings',
        construction: 'construction',
        army: 'army',
        training: 'training',
        research: 'research',
        smithy: 'smithy',
        nobility: 'nobility',
        attack: 'attack',
        support: 'support',
        espionage: 'espionage',
        market: 'market',
      }
      const name = names[feature]
      if (name) return { name, path: url.pathname, worldId, villageId, query }
    }
  }
  return { name: 'not-found', path: url.pathname, query }
}

export const router = reactive<{ current: AppRoute }>({
  current: parseRoute(new URL(window.location.href)),
})

export function navigate(path: string, replace = false): void {
  markUx('navigation:shell')
  markUx('navigation:data')
  const target = parseRoute(new URL(path, window.location.origin))
  prefetchNavigation(target)
  if (replace) history.replaceState({}, '', path)
  else history.pushState({}, '', path)
  router.current = target
  window.scrollTo({ top: 0, behavior: 'auto' })
}

export function initializeRouter(): void {
  const routeAnchor = (event: Event): HTMLAnchorElement | null => {
    const target = event.target
    if (!(target instanceof Element)) return null
    const anchor = target.closest('a[data-router-link]')
    if (!(anchor instanceof HTMLAnchorElement)) return null
    if (anchor.origin !== window.location.origin || event.defaultPrevented)
      return null
    return anchor
  }
  const warmAnchor = (anchor: HTMLAnchorElement): void => {
    prefetchNavigation(
      parseRoute(
        new URL(`${anchor.pathname}${anchor.search}`, window.location.origin),
      ),
    )
  }
  const warm = (event: Event): void => {
    const anchor = routeAnchor(event)
    if (anchor) warmAnchor(anchor)
  }
  let pointerWarmTimer: number | null = null
  const warmPointer = (event: Event): void => {
    const anchor = routeAnchor(event)
    if (!anchor) return
    if (pointerWarmTimer !== null) window.clearTimeout(pointerWarmTimer)
    pointerWarmTimer = window.setTimeout(() => {
      pointerWarmTimer = null
      warmAnchor(anchor)
    }, 85)
  }
  const cancelPointerWarm = (): void => {
    if (pointerWarmTimer === null) return
    window.clearTimeout(pointerWarmTimer)
    pointerWarmTimer = null
  }

  window.addEventListener('popstate', () => {
    markUx('navigation:shell')
    markUx('navigation:data')
    router.current = parseRoute(new URL(window.location.href))
    prefetchNavigation(router.current)
  })
  document.addEventListener('pointerover', warmPointer)
  document.addEventListener('pointerout', cancelPointerWarm)
  document.addEventListener('focusin', warm)
  document.addEventListener('pointerdown', warm)
  document.addEventListener('click', (event) => {
    const anchor = routeAnchor(event)
    if (!anchor) return
    event.preventDefault()
    navigate(`${anchor.pathname}${anchor.search}${anchor.hash}`)
  })
}
